import { createError, ErrorCodes, Logger, AsyncUtil, PlatformUtil } from '@quickplay/foundation';
import { MediatailorInitializationData } from '../../model/MediatailorInitializationData';
import { MediatailorSession } from '../../model/MediatailorSession';
import { MediatailorMetadata } from '../../model/MediatailorMetadata';
import { fetchMediaTailorResponse } from '../MediatailorSessionUtil';
import { AdPlaybackPolicy, Player } from '@quickplay/player-interface';
import { DefaultMediatailorPlayer } from '../DefaultMediatailorPlayer';
import { MediatailorErrorCodes } from '../MediatailorErrorCodes';
import { MediatailorPlayer } from '../../model/MediatailorPlayer';
import { createPalSession, PALConfiguration, PALSession } from '@quickplay/pal';
import { getMediaTailorSessionParams, isHlsManifestUrl } from './Util/MediatailorUtil';
import { StreamStartUpTimeData } from '../../model/StreamStartUpTimeData';
import { MediaTailorTrackingResponse } from '../../model/AdMetadata';
import { AdFrequencyControlConfiguration } from '@quickplay/player';

export function createServerMediatailorSession(): MediatailorSession {
  const DEFAULT_CONNECTION_TIMEOUT_MS = 6_000;
  const DEFAULT_MAX_RETRIES = 1;
  const AWS_SESSION_ID = 'aws.sessionId';
  let palSession: PALSession | undefined = undefined;
  let palNonce: string | undefined = undefined;
  let connectionTimeoutInMS: number = DEFAULT_CONNECTION_TIMEOUT_MS;
  let maxRetries: number = DEFAULT_MAX_RETRIES;
  let hlsAdTrackingPollIntervalInMS: number | undefined = undefined;
  let mediatailorSessionStartTime = 0;
  let mediatailorSessionEndTime = 0;
  let palEndTime = 0;
  let palStartTime = 0;
  const fetchMediatailorTrackingData = async (
    trackingUrl: string,
    connectionTimeoutInMS: number,
    maxRetries: number,
  ): Promise<MediaTailorTrackingResponse> => {
    try {
      return await AsyncUtil.retryOnNetworkFailure(
        () => fetchMediaTailorResponse<MediaTailorTrackingResponse>(trackingUrl, undefined, connectionTimeoutInMS),
        maxRetries,
      );
    } catch (error: any) {
      throw error;
    }
  };

  const extractSessionIdFromManifestUrl = (manifestUrl: string): string | null => {
    try {
      const url = new URL(manifestUrl);
      return url.searchParams.get(AWS_SESSION_ID);
    } catch {
      return null;
    }
  };

  const validateAndFetchTrackingData = async (
    manifestUrl: string,
    trackingUrl: string,
    connectionTimeoutInMS: number,
    maxRetries: number,
  ): Promise<MediaTailorTrackingResponse> => {
    // prefetch manifest to get ad metadata proactively for DASH VOD contents.
    await fetch(manifestUrl);
    return fetchMediatailorTrackingData(trackingUrl, connectionTimeoutInMS, maxRetries);
  };

  const processSessionResponse = async (
    sessionResponse: { [key: string]: any },
    sessionUrl: string,
    fallbackUrl: string,
    connectionTimeoutInMS: number,
    maxRetries: number,
    isLive?: boolean,
  ): Promise<MediatailorMetadata> => {
    if (!sessionResponse?.manifestUrl || !sessionResponse?.trackingUrl) {
      return {
        manifestUrl: fallbackUrl,
        isFallback: true,
        fallbackReason: createError(
          MediatailorErrorCodes.SESSION_DATA_PARSING_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
          sessionResponse?.trackingUrl ? 'Missing manifestUrl' : 'Missing trackingUrl',
        ),
      };
    }

    const manifestUrl = new URL(sessionResponse.manifestUrl, sessionUrl).href;
    const trackingUrl = new URL(sessionResponse.trackingUrl, sessionUrl).href;

    const adSessionId = extractSessionIdFromManifestUrl(manifestUrl);

    let adMetadata: MediaTailorTrackingResponse | undefined = undefined;
    try {
      // Developer Note:
      // Proactively validating an ad metadata for VOD streams is to increase the accuracy in Ad playback. There are below cases considered to avoid unnecessary operations:
      // Case 1: In Apple platforms, Ad metadata is fetched only when variant manifest call is made. So, proactive check of ad metadata is not possible before content load.
      // Case 2: In non-Apple platforms, ad metadata can be fetched proactively before content load.
      // Case 3: This is not applicable for live since live playback required periodic ping ad tracking api.
      // Case 4: HLS on any platform (e.g. Chrome with Shaka) only gets avails once the variant playlist is requested,
      // so a prefetch after the master playlist returns empty avails. The ad handler fetches it after manifest parsing instead.
      if (!PlatformUtil.isApple() && !isLive && !isHlsManifestUrl(manifestUrl)) {
        adMetadata = await validateAndFetchTrackingData(manifestUrl, trackingUrl, connectionTimeoutInMS, maxRetries);
      }

      return {
        manifestUrl: manifestUrl,
        isFallback: false,
        trackingUrl: trackingUrl,
        adMetadata: adMetadata,
        adSessionId: adSessionId || undefined,
      };
    } catch (error: any) {
      return {
        manifestUrl: fallbackUrl,
        isFallback: true,
        fallbackReason: createError(
          MediatailorErrorCodes.TRACKING_API_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
          'Failed to fetch Ad metadata',
          error,
        ),
        adSessionId: adSessionId || undefined,
      };
    }
  };

  return {
    initialize: async (
      mediatailorInitializationData: MediatailorInitializationData,
      palConfiguration?: PALConfiguration,
    ): Promise<MediatailorMetadata> => {
      connectionTimeoutInMS = mediatailorInitializationData?.connectionTimeoutInMS || DEFAULT_CONNECTION_TIMEOUT_MS;
      maxRetries = mediatailorInitializationData?.maxRetries || DEFAULT_MAX_RETRIES;
      hlsAdTrackingPollIntervalInMS = mediatailorInitializationData?.hlsAdTrackingPollIntervalInMS;
      const sessionUrl = mediatailorInitializationData?.sessionUrl;
      const mediatailorSessionConfiguration = mediatailorInitializationData?.mediatailorSessionConfiguration || {};
      if (palConfiguration) {
        palStartTime = performance.now();
        palSession = createPalSession(true);
        palNonce = await palSession.initialize(palConfiguration);
        palEndTime = performance.now();
        console.log(`[PAL] Session initialization took ${palEndTime - palStartTime}ms to complete`);
      }
      try {
        mediatailorSessionStartTime = performance.now();
        const sessionResponse = await AsyncUtil.retryOnNetworkFailure(() => {
          const requestBody = getMediaTailorSessionParams(mediatailorSessionConfiguration, palNonce);

          return fetchMediaTailorResponse<{ [key: string]: any }>(
            sessionUrl,
            { body: requestBody },
            connectionTimeoutInMS,
            'POST',
          );
        }, maxRetries);
        mediatailorSessionEndTime = performance.now();

        return await processSessionResponse(
          sessionResponse,
          sessionUrl,
          mediatailorInitializationData.fallbackUrl,
          connectionTimeoutInMS,
          maxRetries,
        );
      } catch (error: any) {
        // DEV NOTE:
        // Ensure end time is captured even when MediaTailor session initialization fails.
        // Without this, ssaiStreamUrlFetchTimeInMS may become negative
        // (endTime would remain 0 while startTime was already set).
        // This is especially observed when session init fails.
        mediatailorSessionEndTime = performance.now();

        return {
          manifestUrl: mediatailorInitializationData.fallbackUrl,
          isFallback: true,
          fallbackReason: createError(
            MediatailorErrorCodes.SESSION_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
            'Mediatailor Session initialization Failed',
            error,
          ),
        };
      }
    },

    createMediatailorPlayer: (
      player: Player,
      mediatailorMetadata?: MediatailorMetadata,
      adPlaybackPolicy?: AdPlaybackPolicy,
      logger?: Logger,
      _videoElement?: HTMLVideoElement,
      adFrequencyControl?: AdFrequencyControlConfiguration,
    ): MediatailorPlayer => {
      const streamStartUpTimeData: StreamStartUpTimeData = {
        ssaiStreamUrlFetchTimeInMS: Number((mediatailorSessionEndTime - mediatailorSessionStartTime).toFixed(2)),
        palTokenFetchTimeInMS: Number((palEndTime - palStartTime).toFixed(2)),
      };
      return new DefaultMediatailorPlayer(
        player,
        mediatailorMetadata,
        palSession,
        adPlaybackPolicy,
        streamStartUpTimeData,
        connectionTimeoutInMS,
        maxRetries,
        hlsAdTrackingPollIntervalInMS,
        logger,
        adFrequencyControl,
      );
    },
  };
}
